/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { usePrevious } from "@react-hookz/web";
import { ThemeKey } from "@smartsheet/design-tokens";
import { warnOnce } from "@smartsheet/lodestar-utils";
import { getClientId } from "@smartsheet/lodestar-utils";
import { useForkRef } from "@smartsheet/lodestar-utils";
import { useActive } from "@smartsheet/lodestar-utils";
import classNames from "classnames";
import { useCombobox, UseComboboxStateChange } from "downshift";
import {
    ButtonHTMLAttributes,
    ChangeEvent,
    HTMLAttributes,
    InputHTMLAttributes,
    KeyboardEvent,
    RefAttributes,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTheme } from "../../../../theme/utils/useTheme";
import { InputFieldBaseProps } from "../../../../util/inputFieldBaseProps";
import { useHovered } from "../../../../util/useHovered";
import { FlyoutProps } from "../../../flyout";
import { useFocusMode } from "../../../focusMode";
import { FORM_LABEL_CLICK_EVENT_ID } from "../../../formlabel/FormLabel";
import { SELECT_TOGGLE_ELEMENT_CLASS } from "../../constants";
import { useA11yStatusMessages } from "../../hooks/useA11yStatusMessages";
import { useAutoHighlightWithLoading } from "../../hooks/useAutoHighlightWithLoading";
import { useBlurOnChipBlur } from "../../hooks/useBlurChipOnBlur";
import { useHorizontalResize } from "../../hooks/useHorizontalResize";
import { useMultipleSelectionCustom } from "../../hooks/useMultipleSelectionCustom";
import { useMultiSelectOptionToString } from "../../hooks/useOptionToString";
import { useResetSelection } from "../../hooks/useResetSelection";
import { useSelectFlyout } from "../../hooks/useSelectFlyout";
import { highlightFirstAutomaticallyStateReducer } from "../../reducers/highlightFirstAutomaticallyStateReducer";
import { BaseOptionType, FormatOption, ResetFilteringReason } from "../../types";
import {
    useSelectIds,
    isSelectAllMarkerOption,
    isCreatableMarkerOption,
    creatableMarkerOption,
    stopPropagationHandler,
    isSelectV2OptionSeparator,
    findNextValidOptionIndex,
    squashSeparators,
    sortFilteredItems,
} from "../../utils";
import { Actions } from "../shared/Actions";
import { AutoSizeInput } from "../shared/AutoSizeInput";
import { FuzzySearchResult } from "../shared/FuzzySearchResult";
import { HiddenInput } from "../shared/HiddenInput";
import { Root } from "../shared/Root";
import { SelectClearButton } from "../shared/SelectClearButton";
import { SelectMenu } from "../shared/SelectMenu";
import { SelectPlaceholder } from "../shared/SelectPlaceholder";
import { ToggleButton } from "../shared/ToggleButton";
import { MultiSelectOption } from "./MultiSelectOption";
import { MultiSelectValueContainer } from "./MultiSelectValueContainer";
import { OptionalPropsNowRequired } from "./types";

export interface MultiSelectComboBoxProps<Option extends BaseOptionType>
    extends InputFieldBaseProps,
        OptionalPropsNowRequired<Option, HTMLInputElement> {
    /**
     * This set of options includes the select all.
     */
    memoizedOptions: Option[];

    /**
     * The logging hook is taken care of in the parent component.
     */
    onChange: (newOptions: Option[]) => void;
}

export const MultiSelectComboBox = <Option extends BaseOptionType>({
    appearance = "outlined",
    "aria-describedby": ariaDescribedby,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-required": ariaRequired,
    classes,
    className,
    clientId,
    enableSelectAll,
    formatOption,
    getHiddenInputValue,
    hiddenInputName,
    id,
    innerRef,
    isClearable = false,
    isLoading,
    isOptionDisabled,
    inputCharacterLimit,
    inputStyle,
    isReadonly,
    menuWidth = "match-control",
    maxMenuHeight,
    maxOptionsUntilVirtualization,
    minHeight,
    maxSelectedRows,
    menuId,
    rollupSelectionAfter,
    memoizedOptions,
    menuPlacement = "bottom",
    menuPosition,
    onBlur,
    onChange,
    onCreateOption,
    onFocus,
    optionHeight,
    options: providedOptions,
    optionToString,
    portalProps,
    selectedOptions,
    validationState,
    filterOption,
    sortFilteredOptions,
    getOptionKey,
    menuIsOpen,
    setMenuIsOpen,
    onMenuIsOpenChanged,
    useFuzzySearch = false,
    inputValue: providedInputValue,
    setInputValue: providedSetInputValue,
    hideDropdownArrow = false,
    formatSelectedValue,
    placeholder,
    onKeyDown: providedOnKeyDown,
    highlightFirstOptionAutomatically = false,
    onPaste: providedOnPaste,
    shouldResetFiltering = () => true,
    inputFieldHeight,
    shouldDisableClearingOnInputClicks = false,
    shouldNotAutoHighlightAfterSelect = false,
    overrides,
}: MultiSelectComboBoxProps<Option>) => {
    const a11yMessages = useA11yStatusMessages();
    const { key } = useTheme();
    const themeMode = key as ThemeKey;

    // null here means "no filter applied" so we can easily know to use the normal options object
    // when there's no filter.
    const [filteredOptions, setFilteredOptions] = useState<Option[] | null>(null);
    const [inputValue, setInputValue] = useState(providedInputValue ?? "");
    const [isFocused, setIsFocused] = useState(false);

    // Tracks the current height of the content box containing the Chips.
    // Update this to a new value to reposition the Flyout.
    // 20/24 are the default chip sizes.  Default to them to prevent a re-render for default sized comboboxes.
    const [chipContentHeight, setChipContentHeight] = useState(inputFieldHeight === 40 ? 24 : 20);

    // The flyout needs to point to the component container, but otherwise flyout interactions
    // should affect the input.
    const { ref: resizeRef, width: rootWidth } = useHorizontalResize<HTMLDivElement>();
    const localContainerRef = useRef<HTMLDivElement>(null);
    const containerRef = useForkRef(localContainerRef, resizeRef);
    const localInputRef = useRef<HTMLInputElement>(null);
    const inputRef = useForkRef(localInputRef, innerRef ? innerRef : null);
    const { focusMode } = useFocusMode();
    const changeType = useRef<string | null>(null);

    const isInteractive = !isReadonly;

    const resetInputAndFilteredOptions = () => {
        setInputValue("");
        providedSetInputValue?.("");
        setFilteredOptions(null);
    };

    const resetFiltering = (reason: ResetFilteringReason) => {
        if (reason === "option-selected") {
            resetInputAndFilteredOptions();
        }
        if (
            !shouldResetFiltering(
                {
                    highlightedIndex,
                    // pass optionsToDisplay to match highlightedIndex
                    options: optionsToDisplay,
                },
                reason
            )
        ) {
            return;
        }
        resetInputAndFilteredOptions();
    };

    const multiSelectOptionToString = useMultiSelectOptionToString(optionToString, inputValue);

    const {
        isHovered: isToggleHovered,
        onMouseLeave: onToggleMouseLeave,
        onMouseEnter: onToggleMouseEnter,
    } = useHovered<HTMLButtonElement>();

    const {
        isActive: isToggleActive,
        onMouseDown: onTAMouseDown,
        onMouseEnter: onTAMouseEnter,
        onMouseLeave: onTAMouseLeave,
    } = useActive({
        onMouseEnter: onToggleMouseEnter,
        onMouseLeave: onToggleMouseLeave,
    });

    const optionsToDisplay = filteredOptions || memoizedOptions;
    const isCreatable = typeof onCreateOption === "function";

    const doFilter = useCallback(
        (forInputValue: string) => {
            if (!forInputValue) {
                return;
            }

            const inputLowerCase = forInputValue.toLowerCase();
            const fuzzySearchScoreMap = new Map<Option, number>();
            const filteredItems = memoizedOptions.filter((opt) => {
                // Ignore this one out here so we don't accidentally call optionToString on
                // it.
                if (isSelectAllMarkerOption(opt)) {
                    return false;
                } else if (isSelectV2OptionSeparator(opt)) {
                    return true;
                }
                return filterOption(opt, inputLowerCase, fuzzySearchScoreMap);
            });
            const sortedAndFiltered = sortFilteredOptions
                ? sortFilteredOptions(fuzzySearchScoreMap, filteredItems)
                : sortFilteredItems(fuzzySearchScoreMap, filteredItems);
            const squashedAndFilteredItems = squashSeparators(sortedAndFiltered);
            if (isCreatable && forInputValue) {
                // prevent option to add duplicate values to the menu
                if (
                    squashedAndFilteredItems.filter((option: Option) => {
                        // This is a subpar solution, but for now it's a fix for the MultiContactPicker
                        // where `value` does not exist, but `id` does.
                        const value = option.value ?? option.id;
                        return (value as string)?.toLowerCase() === inputLowerCase;
                    }).length
                ) {
                    // don't render the add item in the menu
                    setFilteredOptions(
                        squashedAndFilteredItems.filter((item) => item.value !== creatableMarkerOption.value)
                    );
                    return;
                }
                setFilteredOptions([creatableMarkerOption as unknown as Option, ...squashedAndFilteredItems]);
            } else {
                setFilteredOptions(squashedAndFilteredItems);
            }
        },
        [filterOption, isCreatable, memoizedOptions, sortFilteredOptions]
    );

    const {
        addSelectedItem,
        getDropdownProps,
        getSelectedItemProps,
        isItemSelected,
        removeSelectedItem,
        selectedItems,
        setSelectedItems,
        setActiveIndex,
        activeIndex,
    } = useMultipleSelectionCustom({
        isInteractive,
        itemToString: multiSelectOptionToString,
        rollupSelectionAfter,
        onChange,
        selectedItems: selectedOptions,
    });

    const { resetSelection } = useResetSelection();
    const selectIds = useSelectIds("combobox", id);

    const {
        closeMenu,
        getComboboxProps,
        getInputProps,
        getItemProps,
        getMenuProps,
        getToggleButtonProps,
        highlightedIndex,
        isOpen,
        toggleMenu,
        openMenu,
        setHighlightedIndex,
        selectItem,
    } = useCombobox({
        ...a11yMessages,
        ...selectIds,
        ...(menuIsOpen !== undefined ? { isOpen: menuIsOpen } : null),
        circularNavigation: false,
        items: optionsToDisplay,
        inputValue,
        itemToString: multiSelectOptionToString,
        onInputValueChange: ({ inputValue, isOpen }: UseComboboxStateChange<Option>) => {
            // Don't try and filter when the menu is closed. This can be triggered by
            // the selection changing.
            if (isOpen && inputValue) {
                doFilter(inputValue);
            } else {
                resetFiltering("closed-menu-input-value-change");
            }
        },
        onIsOpenChange: ({ isOpen: openState }) => {
            if (process.env.NODE_ENV !== "production") {
                warnOnceForDeprecatedSetMenuIsOpenProp =
                    warnOnceForDeprecatedSetMenuIsOpenProp ??
                    warnOnce(
                        "MultiSelectV2 (combobox): setMenuIsOpen is deprecated. Please use onMenuIsOpenChanged instead."
                    );
                if (setMenuIsOpen) {
                    warnOnceForDeprecatedSetMenuIsOpenProp();
                }
            }
            // setMenuIsOpen is deprecated. Use onMenuIsOpenChanged if it's available instead.
            (onMenuIsOpenChanged ?? setMenuIsOpen)?.(!!openState);
        },
        stateReducer: (state, actionAndChanges) => {
            const { changes, type } = actionAndChanges;
            changeType.current = type;
            // Cancel the following events if the input is non-interactive.
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEscape:
                    // Cancel the escape key handling. We want to handle this key on our own
                    // through the StackOrderManager.
                    return state;

                case useCombobox.stateChangeTypes.InputKeyDownArrowDown:
                case useCombobox.stateChangeTypes.InputKeyDownArrowUp:
                case useCombobox.stateChangeTypes.InputKeyDownHome:
                case useCombobox.stateChangeTypes.InputKeyDownEnd:
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ToggleButtonClick:
                    if (!isInteractive) {
                        return state;
                    }
            }

            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    if (changes.selectedItem && isOptionDisabled?.(changes.selectedItem)) {
                        return state;
                    }
                    return {
                        ...changes,
                        // Retain the menu's state after a selection.
                        highlightedIndex: state.highlightedIndex,
                        isOpen: true,
                    };
            }

            if (changes.highlightedIndex) {
                switch (type) {
                    case useCombobox.stateChangeTypes.InputKeyDownArrowUp:
                    case useCombobox.stateChangeTypes.InputKeyDownArrowDown:
                        if (isSelectV2OptionSeparator(optionsToDisplay[changes.highlightedIndex])) {
                            return {
                                ...changes,
                                highlightedIndex: findNextValidOptionIndex(
                                    optionsToDisplay,
                                    changes.highlightedIndex,
                                    type === useCombobox.stateChangeTypes.InputKeyDownArrowDown
                                ),
                            };
                        }
                }
            }
            //look to see if the input has any characters in it,
            //if so then highlightFirstOptionAutomatically can apply its functionality
            const inputHasItems = state.inputValue.length > 0 || selectedItems.length > 0;
            if (highlightFirstOptionAutomatically && inputHasItems) {
                return highlightFirstAutomaticallyStateReducer<Option>(optionsToDisplay, state, actionAndChanges);
            }

            return changes;
        },
        onStateChange: ({ type, selectedItem }) => {
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    if (selectedItem) {
                        if (enableSelectAll && isSelectAllMarkerOption(selectedItem)) {
                            if (!allOrSomeSelected) {
                                setSelectedItems(providedOptions);
                            } else {
                                setSelectedItems([]);
                            }
                            resetSelection(selectItem, 10);
                        } else if (isCreatable && isCreatableMarkerOption(selectedItem) && inputValue) {
                            onCreateOption(inputValue);
                            // Gotta clear out the selected value here as well - the creatable
                            // option is already selected by downshift, so we need to ensure it's
                            // de-selected after the state change completes.
                            resetSelection(selectItem, 10);
                        } else {
                            if (isItemSelected(selectedItem)) {
                                removeSelectedItem(selectedItem);
                                // use a time < 200 ms because the default message that useSelect
                                // reads will debounce anything under 200ms and not call it's
                                // default handler. This means something will be deselected and the
                                // useMultipleSelection a11y status message will be the only one
                                // read out since from useSelect's perspective, the selectedItem
                                // changed from null to null. No change = an empty string for the
                                // a11y message.
                                resetSelection(selectItem, 10);
                            } else {
                                addSelectedItem(selectedItem);
                                // Use a timeout greater than 200ms. This means we'll allow
                                // useSelect to run its default a11y message updater and then
                                // reset the selected item to null.
                                // If we don't do this, there will be no message read on selection.
                                resetSelection(selectItem, 220);
                            }
                        }
                    }
                    // For any selection, clear things out.
                    resetFiltering("option-selected");
                    break;
                case useCombobox.stateChangeTypes.InputBlur:
                    resetFiltering("input-blur");
                    break;
                // If no results are found and a user closes the menu (either by clicking)
                // the toggle button, the "no results" message, or blur, we want to reset.
                case useCombobox.stateChangeTypes.ToggleButtonClick:
                case useCombobox.stateChangeTypes.FunctionToggleMenu:
                    if (!selectedItem) {
                        resetFiltering("toggle-menu");
                    }
                    break;
                default:
                    break;
            }
        },
    });

    const { flyoutProps, targetRef, ariaOwns, ariaControls } = useSelectFlyout({
        menuId,
        clientId,
        closeMenu,
        isOpen,
        targetRef: containerRef,
        menuPlacement,
        menuPosition,
        portalProps,
        // Update positioning whenever the open state changes, or when the combobox height changes.
        // We need the open state because if it's closed, and something else moves the combobox, the
        // menu needs to recalc or it will still be pointing at the position of its initial render.
        updateKey: `${String(isOpen)}_${chipContentHeight}`,
    });

    // We must ignore the added internal options like "create X" and select all here.
    const allSelected = providedOptions.length === selectedItems.length;
    // Incorporate some so that select all functionality only shows when 0 items are selected
    const allOrSomeSelected = providedOptions.length === selectedItems.length || selectedItems.length > 0;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const downshiftInputProps = getInputProps({
        value: inputValue,
        "aria-describedby": ariaDescribedby,
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledby,
        "aria-required": ariaRequired,
        ...getDropdownProps({
            ref: inputRef,
            onPaste: providedOnPaste,
            onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
                providedOnKeyDown?.(e, {
                    highlightedIndex,
                    options: filteredOptions,
                });
                switch (e.key) {
                    case "Tab":
                        if (isOpen) {
                            e.preventDefault();
                            localInputRef.current?.focus();
                            closeMenu();
                            resetFiltering("key-down-tab");
                        }
                        break;
                    case "Escape":
                        resetFiltering("key-down-escape");
                }
            },
        }),
        "aria-controls": ariaControls,
        /**
         * inputValue has to be updated here due updates in different ticks as normal behavior.
         * Setting inputValue here avoids cursor resetting after input value modifications.
         */
        onChange: (e) => {
            const val = (e as ChangeEvent<HTMLInputElement>).target.value;
            const validInput =
                // Account for multi-byte characters by using `[].length` instead of `"".length`.
                inputCharacterLimit && [...val].length > inputCharacterLimit
                    ? val.substring(0, inputCharacterLimit)
                    : val;
            setInputValue(validInput);
            providedSetInputValue?.(validInput);
        },
    }) as InputHTMLAttributes<HTMLInputElement> & RefAttributes<HTMLInputElement>;

    // There are only a couple things we want to remove from this set of props.
    // We really want the input to still be focusable and interactive, just not do anything.
    if (!isInteractive && downshiftInputProps) {
        downshiftInputProps.onChange = undefined;
        // downshiftInputProps.onKeyDown = undefined;
        downshiftInputProps.readOnly = true;
    }

    const optionRenderer: FormatOption<Option> = (
        option,
        index,
        selectedIndex,
        optionSpreadProps,
        isOptionSelected,
        metadata
    ) => {
        let optionLabel: string | JSX.Element = optionToString(option);

        if (useFuzzySearch) {
            optionLabel = (
                <FuzzySearchResult inputValue={metadata.inputValue} option={option} optionToString={optionToString} />
            );
        }

        return (
            <MultiSelectOption
                className={classNames("sds-multi-select-option", {
                    "highlight-first-option-auto": highlightFirstOptionAutomatically,
                })}
                height={optionHeight}
                checkedState={isItemSelected(option) ? "checked" : "unchecked"}
                aria-disabled={metadata.isDisabled}
                {...optionSpreadProps}
            >
                {optionLabel}
            </MultiSelectOption>
        );
    };

    const showClearAction = isClearable && selectedItems.length > 0 && !isReadonly;

    const handleChipNavigation = (direction: "tab" | "down" | "up") => {
        switch (direction) {
            case "tab":
                localInputRef.current?.focus();
                break;
            case "down":
                openMenu();
                setActiveIndex(-1);
                setHighlightedIndex(0);
                break;
            case "up":
                openMenu();
                setActiveIndex(-1);
                setHighlightedIndex(memoizedOptions.length - 1);
                break;
        }
    };

    useBlurOnChipBlur({
        ref: localContainerRef,
        activeIndex: activeIndex,
        setActiveIndex: setActiveIndex,
    });

    const onChipClick: (e: React.MouseEvent<HTMLDivElement>) => void = (e: React.MouseEvent<HTMLDivElement>) => {
        stopPropagationHandler(e);
        if (isOpen) {
            closeMenu();
        }
    };

    useEffect(() => {
        if (providedInputValue !== undefined && providedInputValue !== inputValue) {
            setInputValue(providedInputValue);
        }
    }, [doFilter, inputValue, providedInputValue]);

    // If options changes, we'll have to re-run the filtering step and re-render.
    // This is usually for async-loaded options.
    const previousOptions = usePrevious(providedOptions);
    useEffect(() => {
        if (previousOptions !== providedOptions) {
            // Either the user is controlling the input value, or use the uncontrolled input value
            // from downshift.
            if (inputValue) {
                doFilter(inputValue);
            } else {
                setFilteredOptions(null);
            }
        }
    }, [doFilter, providedOptions, previousOptions, inputValue]);

    const internalHighlightFirstOptionAutomatically =
        highlightFirstOptionAutomatically && (!!inputValue || selectedItems.length > 0);

    useAutoHighlightWithLoading<Option>(
        setHighlightedIndex,
        optionsToDisplay,
        isLoading,
        internalHighlightFirstOptionAutomatically,
        isCreatable,
        inputValue,
        shouldNotAutoHighlightAfterSelect
    );

    const handleMultiSelectComboBoxFocus = (event: React.FocusEvent<HTMLDivElement>, type: "blur" | "focus") => {
        //this is to make sure the focus events are only triggered when the container is focused.
        if (event.target.tagName === "INPUT") {
            if (type === "focus" && document.activeElement === localInputRef.current && !isOpen) {
                setIsFocused(true);
                onFocus?.(event as unknown as React.FocusEvent<HTMLInputElement>);
            }
            //only fires the passed in blur event when the referring element/relatedTarget is not the listbox
            //the event should only happen when the flyout is closed and not when selecting options within the flyout
            //the isOpen state is true in both cases when the blur event takes place, so we had to add the relatedTarget check
            if (
                type === "blur" &&
                (event.relatedTarget === null ||
                    (typeof event?.relatedTarget?.getAttribute === "function" &&
                        event?.relatedTarget?.getAttribute("role") !== "listbox"))
            ) {
                setIsFocused(false);
                onBlur?.(event as unknown as React.FocusEvent<HTMLInputElement>);
            }
        }
    };

    const comboBoxProps = useMemo(() => {
        return getComboboxProps({
            ref: targetRef,
            onClick: (e) => {
                e.stopPropagation();
                //if we know the click is dispatched from the FormLabel,
                //we want to open the menu if the change isn't the input blur
                //this is because the input blur fires before the click event
                //causing the menu to flash closed then reopen (see FormLabel.tsx)
                if (e.detail === FORM_LABEL_CLICK_EVENT_ID) {
                    if (!isOpen && changeType.current !== useCombobox.stateChangeTypes.InputBlur) {
                        openMenu();
                    }
                    changeType.current = null;
                } else if (isInteractive) {
                    // To prevent the menu from opening/closing on every click (even if a user has typed text in)
                    // this call should be gated behind downshiftInputProps.value, only firing when falsy.
                    // To avoid a breaking change, use the shouldDisableClearingOnInputClicks prop to opt-in to this behavior.
                    if (shouldDisableClearingOnInputClicks) {
                        if (!isOpen) {
                            toggleMenu();
                        }
                    } else {
                        toggleMenu();
                    }
                }
                // Still focus even if we are disabled.
                localInputRef.current?.focus();
            },
            "aria-owns": ariaOwns,
        }) as HTMLAttributes<HTMLDivElement>;
    }, [
        ariaOwns,
        getComboboxProps,
        isInteractive,
        isOpen,
        shouldDisableClearingOnInputClicks,
        targetRef,
        toggleMenu,
        openMenu,
    ]);

    useEffect(() => {
        if (!isOpen) {
            //wait a tick before resetting the changeType, so it's the last thing
            //to run in the event loop. This is important because
            //we do this so label clicks can distinguish between
            //input blurs that happen when it's clicked vs
            //blurs from other element clicks.
            setTimeout(() => (changeType.current = null), 0);
        }
    }, [isOpen]);

    return (
        <Root
            inputFieldHeight={inputFieldHeight}
            data-client-id={getClientId(clientId, "-root")}
            className={className}
            isFocused={isFocused || isOpen}
            validationState={validationState || "rest"}
            isReadonly={isReadonly}
            focusMode={focusMode}
            themeMode={themeMode}
            appearance={appearance}
            onBlur={(e: React.FocusEvent<HTMLDivElement>) => handleMultiSelectComboBoxFocus(e, "blur")}
            onFocus={(e: React.FocusEvent<HTMLDivElement>) => handleMultiSelectComboBoxFocus(e, "focus")}
            aria-activedescendant={downshiftInputProps["aria-activedescendant"]}
            aria-controls={downshiftInputProps["aria-controls"]}
            {...comboBoxProps}
            {...overrides?.rootProps}
        >
            <HiddenInput
                clientId={clientId}
                getHiddenInputValue={getHiddenInputValue}
                hiddenInputName={hiddenInputName}
                selectionType={selectedItems}
            />
            <MultiSelectValueContainer
                inputFieldHeight={inputFieldHeight}
                onChipNavigation={handleChipNavigation}
                activeIndex={activeIndex}
                minHeight={minHeight}
                maxSelectedRows={maxSelectedRows}
                numSelected={selectedItems.length}
                onHeightChange={setChipContentHeight}
                getSelectedItemProps={getSelectedItemProps}
                isReadonly={isReadonly}
                className={classNames(classes?.input, SELECT_TOGGLE_ELEMENT_CLASS)}
                rollupSelectionAfter={rollupSelectionAfter}
                optionToString={optionToString}
                removeSelectedItem={removeSelectedItem}
                selectedItems={selectedItems}
                setActiveIndex={setActiveIndex}
                setSelectedItems={setSelectedItems}
                formatSelectedValue={formatSelectedValue}
                id={id}
                clientId={clientId}
                onChipClick={onChipClick}
                {...overrides?.valueContainerProps}
            >
                {
                    <AutoSizeInput
                        inputFieldHeight={inputFieldHeight}
                        clientId={clientId}
                        isReadonly={isReadonly}
                        style={inputStyle}
                        {...downshiftInputProps}
                    />
                }
                {selectedOptions.length == 0 && placeholder && !downshiftInputProps.value && (
                    <SelectPlaceholder inputFieldHeight={inputFieldHeight} hasContentBefore={false}>
                        {placeholder}
                    </SelectPlaceholder>
                )}
            </MultiSelectValueContainer>
            {isInteractive && (
                <Actions inputFieldHeight={inputFieldHeight} themeMode={themeMode}>
                    <ToggleButton
                        inputFieldHeight={inputFieldHeight}
                        clientId={clientId}
                        isReadonly={isReadonly}
                        hideDropdownArrow={hideDropdownArrow}
                        isHovered={isToggleHovered}
                        isActive={isToggleActive}
                        isOpen={isOpen}
                        onMouseEnter={onTAMouseEnter}
                        onMouseLeave={onTAMouseLeave}
                        onMouseDown={onTAMouseDown}
                        {...(getToggleButtonProps({
                            // Don't let the container handle this.
                            onClick: stopPropagationHandler,
                        }) as ButtonHTMLAttributes<HTMLButtonElement>)}
                    />
                    {showClearAction && (
                        <SelectClearButton
                            inputFieldHeight={inputFieldHeight}
                            closeMenu={closeMenu}
                            setActiveIndex={setActiveIndex}
                            clearSelectedItems={() => setSelectedItems([])}
                            refocusTargetElement={() => {
                                localInputRef.current?.focus();
                            }}
                            isMultiSelect={true}
                            resetFiltering={resetFiltering}
                        />
                    )}
                </Actions>
            )}
            <SelectMenu<Option>
                formatOption={formatOption || optionRenderer}
                isLoading={isLoading}
                menuWidth={menuWidth}
                className={classes?.menu}
                maxMenuHeight={maxMenuHeight}
                maxOptionsUntilVirtualization={maxOptionsUntilVirtualization}
                optionHeight={optionHeight}
                getItemProps={getItemProps}
                getMenuProps={getMenuProps}
                highlightedIndex={highlightedIndex}
                isOpen={isOpen}
                isOptionDisabled={isOptionDisabled}
                flyoutProps={{ ...flyoutProps, tabIndex: -1, ...overrides?.selectFlyoutProps } as FlyoutProps} //tabIndex makes the flyout focusable so we can know it's open upon the blur event via the relatedTarget key.
                optionToString={optionToString}
                options={optionsToDisplay}
                targetWidth={rootWidth}
                allSelected={allSelected}
                enableSelectAll={enableSelectAll}
                inputValue={inputValue}
                selectedItems={selectedItems}
                getOptionKey={getOptionKey}
                extraMenuProps={{
                    "aria-multiselectable": isOpen ? true : undefined,
                    ...overrides?.menuProps,
                }}
            />
        </Root>
    );
};

let warnOnceForDeprecatedSetMenuIsOpenProp: undefined | (() => void);
